import { Card } from "@solomon-apps/shared/src/components/ui/card";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React, { useEffect, useRef, useState } from "react";

import {
    formatLongDate,
    formatUsd,
    solscanTxUrl,
    truncateHash
} from "../../shared/format";
import { graphColor, type GraphSlot } from "../graphColors";
import type { ReportingDistribution } from "../reportingModel";

interface DistributionsInfiniteListProps {
    /** Full ledger in any order — the list sorts newest-first internally. */
    distributions: ReportingDistribution[];
    /** uuid → graph-color slot, produced by `buildPositionColorMap`. */
    colorMap: Record<string, GraphSlot>;
}

/**
 * Page size for the infinite scroll. 25 is large enough that scrolling
 * feels substantive but small enough that a year of weekly payouts
 * (~52 rows) pages in twice. Bump to 50 if we see heavy users paging
 * 30+ times; document in the reporting intent's Open Questions before
 * doing so.
 */
const PAGE_SIZE = 25;

/**
 * IntersectionObserver-driven infinite list of distributions, newest
 * first. Loads one `PAGE_SIZE` chunk at a time as the sentinel below
 * the last row enters the viewport. Stops on the last page with an
 * end-of-list indicator.
 *
 * The sentinel + incremental visible-count pattern maps cleanly onto
 * a real cursor-paginated endpoint later.
 */
export function DistributionsInfiniteList({
    distributions,
    colorMap
}: DistributionsInfiniteListProps): React.ReactElement {
    // Sort once per `distributions` reference — newest first.
    const sorted = React.useMemo(
        () =>
            distributions.slice().sort((a, b) => b.timestampMs - a.timestampMs),
        [distributions]
    );

    const [visibleCount, setVisibleCount] = useState<number>(
        Math.min(PAGE_SIZE, sorted.length)
    );
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    // Transient "we're fetching the next page" indicator so the user has
    // feedback while the new rows mount. In real-API land this flips
    // via the fetch state; in mock-land we simulate a short delay.
    const [isPaging, setIsPaging] = useState<boolean>(false);

    // Re-initialise when the underlying dataset size shrinks below the
    // current visible count (e.g. variant switches between tests).
    useEffect(() => {
        setVisibleCount((prev) =>
            Math.min(Math.max(prev, PAGE_SIZE), sorted.length)
        );
    }, [sorted.length]);

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        if (visibleCount >= sorted.length) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (!entry.isIntersecting) return;
                setIsPaging(true);
                // Small delay so the "Loading…" indicator is visible on
                // fast hardware. Purely cosmetic for mock mode.
                window.setTimeout(() => {
                    setVisibleCount((prev) =>
                        Math.min(prev + PAGE_SIZE, sorted.length)
                    );
                    setIsPaging(false);
                }, 120);
            },
            { rootMargin: "120px 0px" }
        );
        observer.observe(el);
        return (): void => observer.disconnect();
    }, [visibleCount, sorted.length]);

    const visible = sorted.slice(0, visibleCount);
    const reachedEnd = visibleCount >= sorted.length;

    return (
        <Card
            asElement="section"
            border={false}
            rounded={false}
            aria-label="All distributions"
        >
            <div className="flex items-baseline justify-between p-4 border-b border-line-base gap-4 flex-wrap">
                <Heading
                    eyebrow="All distributions"
                    title="Every distribution, on-chain"
                    titleVariant="h2"
                />
                <Text variant="label" className="text-foreground-muted">
                    Showing {visible.length} of {sorted.length}
                </Text>
            </div>

            <ul className="divide-y divide-line-base">
                {visible.map((row) => (
                    <DistributionRow
                        key={row.uuid}
                        row={row}
                        colorMap={colorMap}
                    />
                ))}
            </ul>

            {/* Sentinel — mounts IntersectionObserver; flips to end copy
                when the whole ledger is visible. */}
            <div
                ref={sentinelRef}
                className="px-4 py-6 flex items-center justify-center"
            >
                <Text variant="label" className="text-foreground-muted">
                    {reachedEnd ? (
                        <>
                            End of history — {sorted.length} distribution
                            {sorted.length === 1 ? "" : "s"}
                        </>
                    ) : isPaging ? (
                        "Loading…"
                    ) : (
                        <span className="text-foreground-imprint">
                            Scroll for more
                        </span>
                    )}
                </Text>
            </div>
        </Card>
    );
}

/* ── Row ─────────────────────────────────────────────────── */

function DistributionRow({
    row,
    colorMap
}: {
    row: ReportingDistribution;
    colorMap: Record<string, GraphSlot>;
}): React.ReactElement {
    const total = row.byPosition.reduce((acc, p) => acc + p.amountUsdv, 0);
    return (
        <li className="px-4 py-4 hover:bg-surface-sunken transition-colors">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="flex flex-col gap-0.5 min-w-[140px]">
                    <Text
                        variant="body"
                        render={<span />}
                        className="text-foreground-base"
                    >
                        {formatLongDate(row.timestampMs)}
                    </Text>
                    <Text variant="label" className="text-foreground-muted">
                        {row.sourceLabel}
                    </Text>
                </div>
                <Text variant="number" className="text-foreground-base">
                    {formatUsd(total, { fractionDigits: 2 })}
                </Text>
            </div>

            {/* Per-position breakdown — same row colors as the stacked bar. */}
            <ul className="mt-3 flex flex-col gap-1.5 pl-0">
                {row.byPosition.map((entry) => (
                    <li
                        key={entry.positionUuid + entry.txSignature}
                        className="flex items-baseline justify-between gap-4 flex-wrap"
                    >
                        <Text
                            variant="small"
                            render={<span />}
                            className="inline-flex items-center gap-2 text-foreground-muted"
                        >
                            <span
                                className="h-2.5 w-2.5 rounded-[1px]"
                                style={{
                                    backgroundColor: graphColor(
                                        colorMap[entry.positionUuid] ??
                                            "primary"
                                    )
                                }}
                                aria-hidden
                            />
                            <span>{entry.positionName}</span>
                        </Text>
                        <span className="inline-flex items-baseline gap-4">
                            <Text
                                variant="number"
                                className="text-foreground-base"
                            >
                                {formatUsd(entry.amountUsdv, {
                                    fractionDigits: 2
                                })}
                            </Text>
                            <Text
                                variant="number"
                                render={
                                    <a
                                        href={solscanTxUrl(entry.txSignature)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    />
                                }
                                className="text-foreground-muted underline decoration-line-base underline-offset-2 hover:decoration-accent-base hover:text-accent-base transition-colors"
                            >
                                {truncateHash(entry.txSignature, 6, 6)}
                            </Text>
                        </span>
                    </li>
                ))}
            </ul>
        </li>
    );
}
